import type { AttractionCategory } from '@/config/objects/attraction-categories';
import {
  Breadcrumbs,
  Button,
  ConfirmDialog,
  DetailPageHeader,
  DetailPageHero,
  PageContainer,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin, Pencil, Tag, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from '@startupquickstart/frontend';

const tabs = [
  { label: 'Overview', path: 'overview', icon: Tag },
  { label: 'Attraction Types', path: 'attraction-types', icon: MapPin },
];

export function AttractionCategoryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data, isLoading } = useQuery<{ success: boolean; data: AttractionCategory }>({
    queryKey: ['AttractionCategory', id],
    queryFn: () => apiClient.get(`/attraction-categories/${id}`),
    enabled: !!id,
  });

  const attractionCategory = data?.data;

  const deleteMutation = useMutation({
    mutationFn: async (
      attractionCategoryId: string,
    ): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/attraction-categories/${attractionCategoryId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['AttractionCategory'] });
        navigate('/attraction-categories');
      } else {
        throw new Error(response.error || 'Failed to delete attraction category');
      }
    },
  });

  const handleDelete = () => {
    if (!attractionCategory) return;
    toast.promise(deleteMutation.mutateAsync(attractionCategory.id), {
      loading: 'Deleting attraction category...',
      success: 'Attraction category deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete attraction category',
    });
  };

  useEffect(() => {
    if (id) {
      const basePath = `/attraction-categories/${id}`;
      if (location.pathname === basePath || location.pathname === `${basePath}/`) {
        navigate(`${basePath}/overview`, { replace: true });
      }
    }
  }, [id, location.pathname, navigate]);

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Attraction Categories', href: '/attraction-categories' },
    { label: attractionCategory?.name || 'Attraction Category', current: true },
  ];

  const basePath = `/attraction-categories/${id}`;
  const imageUrl = attractionCategory?.featuredImage?.url ?? null;

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-lg font-medium mb-2">Attraction Category Not Found</h2>
          <p className="text-muted-foreground">
            The requested attraction category could not be found.
          </p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer padding="none" className="flex flex-col overflow-hidden">
      <DetailPageHeader
        actions={
          attractionCategory && (
            <>
              <Button variant="outline" onClick={() => navigate(`${basePath}/edit`)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          )
        }
      >
        <Breadcrumbs items={breadcrumbs} />
      </DetailPageHeader>

      <DetailPageHero
        isLoading={isLoading}
        category={{ label: 'Attraction Category', icon: Tag }}
        title={attractionCategory?.name || 'Attraction Category'}
        image={{
          url: imageUrl,
          alt: attractionCategory?.name || 'Attraction Category',
          fallbackIcon: Tag,
        }}
        tabs={tabs}
        basePath={basePath}
      >
        <Outlet context={{ attractionCategory, isLoading }} />
      </DetailPageHero>

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Attraction Category"
        description={`Are you sure you want to delete "${attractionCategory?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </PageContainer>
  );
}
